import React, { useMemo, useState } from 'react';
import {
  FaClock,
  FaFilter,
  FaHdd,
  FaInfoCircle,
  FaPython,
} from 'react-icons/fa';
import { FiCheck, FiClock, FiCode, FiX } from 'react-icons/fi';

import {
  formatSubmissionDateTime as formatDateTime,
  formatSubmissionLanguage,
  formatSubmissionErrorDetail,
  resolveSubmissionTestCounts,
  submissionStatusKind as statusKind,
  submissionStatusLabel as statusLabel,
  type SubmissionHistoryItem,
  type SubmissionStatusKind,
} from '../utils/submissionStatus.ts';

export type { SubmissionHistoryItem };

type FilterValue = 'all' | 'accepted' | 'wrong-answer' | 'errors';

function lastAttemptWhen(iso: string, locale: string, t: (k: string) => string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return t('problemPage.submissionHistoryLastToday');
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return t('problemPage.submissionHistoryLastYesterday');
  return formatDateTime(iso, locale);
}

function buildDetail(
  item: SubmissionHistoryItem,
  kind: SubmissionStatusKind,
  t: (k: string) => string,
  defaultTestCount = 0
): { primary: string; secondary?: string } {
  const { passed, total } = resolveSubmissionTestCounts(item, kind, defaultTestCount);

  if (kind === 'accepted' || kind === 'wrong-answer') {
    if (total > 0) {
      return {
        primary: t('problemPage.submissionHistoryTestsPassed')
          .replace('{passed}', String(passed))
          .replace('{total}', String(total)),
      };
    }
    if (passed > 0) {
      return {
        primary: t('problemPage.submissionHistoryTestsPassedShort').replace('{passed}', String(passed)),
      };
    }
  }

  if (kind === 'wrong-answer') {
    return {
      primary: t('problemPage.submissionHistoryTestsPassed')
        .replace('{passed}', '0')
        .replace('{total}', String(defaultTestCount > 0 ? defaultTestCount : '—')),
    };
  }

  if (kind === 'time-limit') {
    const ms = item.execution_time;
    const primary = t('problemPage.submissionHistoryTimeExceeded');
    if (ms != null && ms > 0) {
      const actualSec = (ms / 1000).toFixed(2);
      return {
        primary,
        secondary: t('problemPage.submissionHistoryTimeExceededDetail')
          .replace('{limit}', '2.00')
          .replace('{actual}', actualSec),
      };
    }
    return { primary };
  }

  return { primary: formatSubmissionErrorDetail(item, kind, t) };
}

function matchesFilter(kind: SubmissionStatusKind, filter: FilterValue): boolean {
  if (filter === 'all') return true;
  if (filter === 'accepted') return kind === 'accepted';
  if (filter === 'wrong-answer') return kind === 'wrong-answer';
  return kind !== 'accepted' && kind !== 'wrong-answer';
}

function ErrorExclamationMark() {
  return (
    <svg
      className="submission-history-status-exclamation"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 5v11" />
      <circle cx="12" cy="19" r="1.35" fill="currentColor" stroke="none" />
    </svg>
  );
}

function StatusIcon({ kind }: { kind: SubmissionStatusKind }) {
  if (kind === 'accepted') return <FiCheck aria-hidden strokeWidth={1.75} />;
  if (kind === 'wrong-answer') return <FiX aria-hidden strokeWidth={1.75} />;
  if (kind === 'time-limit') return <FiClock aria-hidden strokeWidth={1.75} />;
  return <ErrorExclamationMark />;
}

export type SubmissionHistoryListProps = {
  submissions: SubmissionHistoryItem[];
  locale: string;
  t: (key: string) => string;
  defaultTestCount?: number;
  onOpenCode: (item: SubmissionHistoryItem) => void;
};

export const SubmissionHistoryList: React.FC<SubmissionHistoryListProps> = ({
  submissions,
  locale,
  t,
  defaultTestCount = 0,
  onOpenCode,
}) => {
  const [filter, setFilter] = useState<FilterValue>('all');

  const filtered = useMemo(() => {
    return submissions.filter((s) => matchesFilter(statusKind(s.status), filter));
  }, [submissions, filter]);

  const subtitle = useMemo(() => {
    if (submissions.length === 0) return '';
    const when = lastAttemptWhen(submissions[0].submitted_at, locale, t);
    return t('problemPage.submissionHistoryMeta')
      .replace('{count}', String(submissions.length))
      .replace('{when}', when);
  }, [submissions, locale, t]);

  return (
    <div className="submission-history">
      <header className="submission-history-header">
        <div className="submission-history-header-top">
          <h2 className="submission-history-title">{t('problemPage.submissionHistoryTitle')}</h2>
          {submissions.length > 0 ? (
            <label className="submission-history-filter">
              <FaFilter className="submission-history-filter-icon" aria-hidden />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterValue)}
                aria-label={t('problemPage.submissionHistoryFilterLabel')}
              >
                <option value="all">{t('problemPage.submissionHistoryFilterAll')}</option>
                <option value="accepted">{t('problemPage.submissionHistoryFilterAccepted')}</option>
                <option value="wrong-answer">{t('problemPage.submissionHistoryFilterWrong')}</option>
                <option value="errors">{t('problemPage.submissionHistoryFilterErrors')}</option>
              </select>
            </label>
          ) : null}
        </div>
        {subtitle ? <p className="submission-history-subtitle">{subtitle}</p> : null}
      </header>

      {filtered.length === 0 ? (
        <p className="submission-history-empty">
          {filter === 'all'
            ? t('problemPage.submissionHistoryEmpty')
            : t('problemPage.submissionHistoryEmptyFilter')}
        </p>
      ) : (
        <ul className="submission-history-list">
          {filtered.map((item) => {
            const kind = statusKind(item.status);
            const detail = buildDetail(item, kind, t, defaultTestCount);

            return (
              <li
                key={item.id}
                className={`submission-history-card submission-history-card--${kind}`}
              >
                <div className="submission-history-card-body">
                  <div className="submission-history-status-col">
                    <div
                      className={`submission-history-status-icon submission-history-status-icon--${kind}`}
                      aria-hidden
                    >
                      <StatusIcon kind={kind} />
                    </div>
                    <span className={`submission-history-status-label submission-history-status-label--${kind}`}>
                      {statusLabel(item.status, kind, t)}
                    </span>
                  </div>

                  <div className="submission-history-meta-col">
                    <div className="submission-history-datetime">
                      {formatDateTime(item.submitted_at, locale)}
                    </div>
                    <div className="submission-history-meta-row">
                      <span className="submission-history-meta-item">
                        {item.language.toLowerCase().includes('python') ? (
                          <FaPython aria-hidden />
                        ) : (
                          <FiCode aria-hidden strokeWidth={1.75} />
                        )}
                        {formatSubmissionLanguage(item.language)}
                      </span>
                      {item.execution_time != null ? (
                        <span className="submission-history-meta-item">
                          <FaClock aria-hidden />
                          {Math.round(item.execution_time)} {t('problemPage.panelTimeMs')}
                        </span>
                      ) : null}
                      {item.memory_usage != null ? (
                        <span className="submission-history-meta-item">
                          <FaHdd aria-hidden />
                          {Number(item.memory_usage).toFixed(1)} {t('problemPage.panelMemoryMb')}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="submission-history-detail-col">
                    <div className="submission-history-detail-primary">{detail.primary}</div>
                    {detail.secondary ? (
                      <div className="submission-history-detail-secondary">{detail.secondary}</div>
                    ) : null}
                  </div>
                </div>

                <footer className="submission-history-card-footer">
                  <button
                    type="button"
                    className="submission-history-open-code"
                    onClick={() => onOpenCode(item)}
                  >
                    <FiCode aria-hidden strokeWidth={1.75} />
                    {t('problemPage.submissionHistoryOpenCode')}
                  </button>
                </footer>
              </li>
            );
          })}
        </ul>
      )}

      {submissions.length > 0 ? (
        <p className="submission-history-footnote">
          <FaInfoCircle aria-hidden />
          {t('problemPage.submissionHistoryFooter')
            .replace('{shown}', String(filtered.length))
            .replace('{total}', String(submissions.length))}
        </p>
      ) : null}
    </div>
  );
};
